import { SwatchesPicker } from "react-color";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { Link, useParams } from "react-router-dom";
import { useAlert, useApi, useTenant } from "../../hooks";
import { useLoaderStore, useUserStore } from "../../context";
import { useEffect, useState } from "react";
import {
  ColorPicker,
  FileUpload,
  Label, TextAreaInput,
  TextInput,
} from "../../components/Inputs";
import useDebounce from "../../hooks/useDebounce";
import { buildUrlWithSubdomain, getFormData } from "../../utils/helpers";
import {Button, PrimaryButton} from "../../components/Buttons";
import useSWR from "swr";
import { isNil } from "lodash/lang";
import { ConfirmationModal } from "../../components/Modals";
import { Grid } from "react-bootstrap-icons";
import {CodeBlock} from "../../components/Displays";
import {useActiveTenant, useTenantTheme} from "../../context/useSystemTenantStore";
import {clsx} from "clsx";
const LayoutSectionCol = ({ children }) => {
  return (
    <div className="flex flex-col bg-gray-100 rounded-lg my-4 p-4 gap-4">
      {children}
    </div>
  );
};
const LayoutSectionRow = ({ children }) => {
  return (
    <div className="flex flex-row bg-gray-100 rounded-lg my-4 p-4 gap-4 align-content-center">
      {children}
    </div>
  );
};
const ItemLabel = ({ label }) => {
  return <h3 className="font-semibold">{label}</h3>;
};
function SystemTenantCustomization(props) {
    const activeTenant = useActiveTenant();
    const theme = useTenantTheme();
  const { updateTheme } = useTenant();
  const SYSTEM_TENANT_ID = activeTenant?.id;
  const { fetch, post, postFormData } = useApi();
  const alert = useAlert();
  const { setShowLoader } = useLoaderStore();
  const [customizationSettings, setCustomizationSettings] = useState({
    id: 0,
    systemTenantName: activeTenant?.name,
    primaryHexColor: theme?.primaryHexColor,
    secondaryHexColor: theme?.secondaryHexColor,
    tertiaryHexColor: theme?.tertiaryHexColor,
    header: theme?.header || "",
    subHeader: theme?.subHeader,
  });
  const [proposedSlug, setProposedSlug] = useState();
  const [isValidSlug, setIsValidSlug] = useState(false);
  const { debounced, updateDebounced } = useDebounce("");
  const [pictureFiles, setPictureFiles] = useState([]);
  const [confirmSlug, setConfirmSlug] = useState(false);
  const { currentUser } = useUserStore();

  const { data: defaultCustomizationSettings, isLoading } = useSWR(
    SYSTEM_TENANT_ID
      ? `systemTenant/GetSystemTenantCustomizationInfo/${SYSTEM_TENANT_ID}`
      : null,
    fetch,
    {
      revalidateOnFocus: false,
    },
  );

  useEffect(() => {
    if (activeTenant) {
      setShowLoader(true);
      getCustomizationDetails();
    }
  }, [activeTenant]);

  useEffect(() => {
    updateDebounced(proposedSlug);
  }, [proposedSlug]);

  useEffect(() => {
    validateSlug();
  }, [debounced]);

  useEffect(() => {
    if (!isNil(defaultCustomizationSettings)) {
      setCustomizationSettings(defaultCustomizationSettings);
    }
  }, [defaultCustomizationSettings]);

  function getCustomizationDetails() {
    fetch(`systemTenant/GetSystemTenantCustomizationInfo/${SYSTEM_TENANT_ID}`)
      .then((res) => {
        if (res) {
          // setCustomizationSettings(res);
          return;
        }
        alert(
          "Theme settings not found",
          "No theme settings found. Lets create one!",
          "warning",
        );
      })
      .catch((err) => {
        alert("Error", "Unable to fetch customization settings", "error");
      })
      .finally(() => {
        setShowLoader(false);
      });
  }
  function handleChange(field, value) {
    setCustomizationSettings({
      ...customizationSettings,
      [field]: value,
    });
  }
  function validateSlug() {
    if (!debounced) return;
    fetch(`systemTenant/ValidateSlug/${debounced}`)
      .then((res) => {
        if (!res.isValid) {
          alert("Invalid subdomain", res.message, "warning");
        }
        setIsValidSlug(res.isValid);
      })
      .catch((err) => {
        alert(
          "Server error",
          "Unable to validate subdomain at this time",
          "error",
        );
      });
  }
  function saveCustomizationDetails(payload = null) {
    setShowLoader(true);
    post(
      `systemTenant/SaveSystemTenantCustomization/${SYSTEM_TENANT_ID}`,
      payload || customizationSettings,
    )
      .then((res) => {
        // setCustomizationSettings(res);
        saveSystemTenantLogo(res.id);
        updateTheme({
            primaryHexColor: customizationSettings.primaryHexColor,
            secondaryHexColor: customizationSettings.secondaryHexColor,
            tertiaryHexColor: customizationSettings.tertiaryHexColor,
            header: customizationSettings.header,
            subHeader: customizationSettings.subHeader
        });
        alert(
          "Saved theme settings",
          "Saved system tenant theme settings",
          "success",
        );
      })
      .catch((err) => {
        alert("Server error", "Unable to save theme settings", "error");
      })
      .finally(() => {
        setShowLoader(false);
        setConfirmSlug(false);
      });
  }
  function saveSystemTenantLogo(passedId) {
    if (!pictureFiles.length) return;
    setShowLoader(true);
    let payload = getFormData(pictureFiles[0], "Photo");
    payload.append("Id", passedId || customizationSettings.id);
    postFormData("systemTenant/SaveSystemTenantLogo", payload)
      .then((res) => {
        // setCustomizationSettings(res);
        setPictureFiles([]);
        alert("Saved logo", "Saved system tenant logo", "success");
      })
      .catch((err) => {
        alert("Server error", "Unable to save system tenant logo", "error");
      })
      .finally(() => {
        setShowLoader(false);
      });
  }
  function handleSave() {
    if (proposedSlug && !customizationSettings.urlSlug) {
      let payload = Object.assign(
        {},
        { ...customizationSettings, urlSlug: proposedSlug },
      );
      saveCustomizationDetails(payload);
      return;
    }
    saveCustomizationDetails();
    // saveSystemTenantLogo();
  }

  function copyText() {
    const textToCopy = document.getElementById('copy-target').value;

    navigator.clipboard.writeText(textToCopy).then(() => {
      // Optional: Provide visual feedback
      alert("Copied to clipboard!");
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  }

  return (
    <div className="overscroll-y-auto px-2">
      <div className={"w-full flex justify-end"}>
        <div className={"mt-2"}>
          <Button text={"Save"} onClick={handleSave} disabled={isLoading}/>
        </div>
      </div>
      <LayoutSectionRow>
          {customizationSettings?.photoUrl &&
          (<img
            className="w-8"
            src={customizationSettings?.photoUrl}
            alt="Logo"
          />)}
        <ItemLabel
          label={`${customizationSettings?.id === 0 ? "Create" : "Update"} ${activeTenant?.name} theme settings`}
        />
      </LayoutSectionRow>
      <LayoutSectionCol>
        {customizationSettings?.urlSlug ? (
            <>
              <ItemLabel label={"Url "}/>
              <div className="flex w-full max-w-md">
                <input
                    type="text"
                    id="copy-target"
                    readOnly
                    className={clsx(
                        "flex-1 min-w-0 block w-full px-4 py-2",
                        "border border-gray-300 rounded-l-lg",
                        " focus:ring-0 focus:border-gray-300",
                        "caret-transparent text-center"
                    )}
                    value={buildUrlWithSubdomain(customizationSettings?.urlSlug)}
                />
                <button
                    title={"Copy Text"}
                    type="button"
                    className="inline-flex items-center px-3 py-2 border-t border-b border-r border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100 focus:z-10 focus:ring-2 focus:ring-blue-500 outline-none"
                    onClick={copyText}
                >
                  <svg className="shrink-0 h-5 w-5 transition text-gray-500" xmlns="http://www.w3.org/2000/svg"
                       viewBox="0 0 20 20" fill="currentColor">
                    <path d="M8 2a1 1 0 000 2h2a1 1 0 100-2H8z"></path>
                    <path
                        d="M3 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v6h-4.586l1.293-1.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L10.414 13H15v3a2 2 0 01-2 2H5a2 2 0 01-2-2V5zM15 11h2a1 1 0 110 2h-2v-2z"></path>
                  </svg>
                </button>

                <button
                    title={"Open in new tab"}
                    type="button"
                    className="inline-flex items-center px-3 py-2 border-t border-b border-r border-gray-300 bg-gray-50 text-gray-500 rounded-r-lg hover:bg-gray-100 focus:z-10 focus:ring-2 focus:ring-blue-500 outline-none"
                    onClick={() => {
                      window.open(
                        buildUrlWithSubdomain(customizationSettings?.urlSlug),
                        "_blank",
                      );
                    }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                       className="bi bi-box-arrow-up-right" viewBox="0 0 16 16">
                    <path fill-rule="evenodd"
                          d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5"/>
                    <path fill-rule="evenodd"
                          d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0z"/>
                  </svg>
                </button>
              </div>
              {/*<CodeBlock*/}
              {/*    text={buildUrlWithSubdomain(customizationSettings?.urlSlug)}*/}
              {/*    copyText={"Copy Url"}*/}
              {/*/>*/}
            </>
        ) : (
            <div>
              <ItemLabel label={"Url Subdomain"}/>
              <TextInput
                  // label={"Url Subdomain"}
                  name={"urlSlug"}
                  value={proposedSlug || ""}
                  onChange={(e) => {
                    setProposedSlug(e.target.value);
                  }}
                  Icon={proposedSlug && !isValidSlug ? XMarkIcon : undefined}
                  iconColor="error"
                  maxLength={20}
                  required
              />
            </div>
        )}
      </LayoutSectionCol>

      <LayoutSectionCol>
        <div className="grid grid-cols-1 gap-4 max-w-3xl">
          <div className="flex flex-col">
            <ItemLabel label="Homepage Header" />
            <TextInput
                value={customizationSettings.header}
                onChange={(e) => handleChange("header", e.target.value)}
                maxLength={200}
            />
          </div>

          <div className="flex flex-col">
            <ItemLabel label="Homepage SubHeader" />
            <TextAreaInput
                value={customizationSettings.subHeader}
                onChange={(e) => handleChange("subHeader", e.target.value)}
                maxLength={200}
            />
          </div>
        </div>
      </LayoutSectionCol>

      <div className="grid grid-cols-5 gap-2">
        <LayoutSectionCol>
          <div>
            <ItemLabel className="" label={"Color Settings"} />
          </div>
          <div className="flex flex-row justify-between">
            <ItemLabel className="" label={"Primary"} />
            <ColorPicker
              color={customizationSettings?.primaryHexColor}
              onChange={(color, event) => {
                handleChange("primaryHexColor", color.hex);
              }}
            />
          </div>
          <div className="flex flex-row  justify-between">
            <ItemLabel className="" label={"Secondary"} />
            <ColorPicker
              color={customizationSettings?.secondaryHexColor}
              onChange={(color, event) => {
                handleChange("secondaryHexColor", color.hex);
              }}
            />
          </div>
          <div className="flex flex-row justify-between">
            <ItemLabel className="" label={"Tertiary"} />
            <ColorPicker
              color={customizationSettings?.tertiaryHexColor}
              onChange={(color, event) => {
                handleChange("tertiaryHexColor", color.hex);
              }}
            />
          </div>
        </LayoutSectionCol>
        <div className="col-span-4">
          <LayoutSectionCol>
            <ItemLabel label={"Logo"} />
            <FileUpload
              files={pictureFiles}
              setFiles={setPictureFiles}
              allowMultipleFiles={false}
              maxFileCount={1}
            />
          </LayoutSectionCol>
        </div>
      </div>
      {/*<LayoutSectionRow>*/}
      {/*  <div className="flex-1 flex flex-col">*/}
      {/*    <ItemLabel className="" label={"Primary Color"} />*/}
      {/*    <div className="shadow-md rounded-lg overflow-hidden">*/}
      {/*      <SwatchesPicker*/}
      {/*        width={"auto"}*/}
      {/*        color={customizationSettings?.primaryHexColor}*/}
      {/*        onChangeComplete={(color, event) => {*/}
      {/*          handleChange("primaryHexColor", color.hex);*/}
      {/*        }}*/}
      {/*      />*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*  <div className="flex-1 flex flex-col">*/}
      {/*    <ItemLabel label={"Secondary Color"} />*/}
      {/*    <div className="shadow-md rounded-lg overflow-hidden">*/}
      {/*      /!*<ColorPicker color={customizationSettings?.secondaryHexColor} onChange={(color, event) => {*!/*/}
      {/*      /!*    handleChange("secondaryHexColor", color.hex);*!/*/}
      {/*      /!*}}/>*!/*/}
      {/*      <SwatchesPicker*/}
      {/*        width={"auto"}*/}
      {/*        color={customizationSettings?.secondaryHexColor}*/}
      {/*        onChangeComplete={(color, event) => {*/}
      {/*          handleChange("secondaryHexColor", color.hex);*/}
      {/*        }}*/}
      {/*      />*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*  <div className="flex-1 flex flex-col">*/}
      {/*    <ItemLabel label={"Tertiary Color"} />*/}
      {/*    <div className="shadow-md rounded-lg overflow-hidden">*/}
      {/*      <SwatchesPicker*/}
      {/*        width={"auto"}*/}
      {/*        color={customizationSettings?.tertiaryHexColor}*/}
      {/*        onChangeComplete={(color, event) => {*/}
      {/*          handleChange("tertiaryHexColor", color.hex);*/}
      {/*        }}*/}
      {/*      />*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*</LayoutSectionRow>*/}


      <ConfirmationModal
        modalOpen={confirmSlug}
        setModalOpen={setConfirmSlug}
        title={"Confirm subdomain"}
        message={`Are you sure you want to set your subdomain to "${proposedSlug}"? This can only be set once.`}
        textToMatch={currentUser.username}
        confirmButtonText={"Confirm"}
        onConfirm={handleSave}
      />
    </div>
  );
}
export default SystemTenantCustomization;
